import { X } from 'lucide-react'
import { Chip } from '@/components/ui/chip'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { CrmIcon } from './CrmIcon'

export interface LabelLike {
  id: string
  name: string
  color: string
  icon: string | null
}

/** A label: neutral pill with a colour dot, or its icon when it has one (ED-13). */
export function LabelChip({
  label,
  onRemove,
  className,
}: {
  label: LabelLike
  onRemove?: () => void
  className?: string
}) {
  return (
    <Chip
      color={label.color}
      shape="pill"
      icon={label.icon ? <CrmIcon name={label.icon} /> : undefined}
      trailing={
        onRemove ? (
          // In the chip's trailing slot, not inside the name: the name is cut off with an
          // ellipsis when it is too long and would clip the button with it. The 20 px
          // button plus the 4 px around it (::after) is a comfortable click target.
          <button
            type="button"
            aria-label={`Label ${label.name} weghalen`}
            onClick={onRemove}
            className="relative inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors outline-none after:absolute after:-inset-1 hover:bg-muted hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <X className="size-3" aria-hidden />
          </button>
        ) : undefined
      }
      className={className}
    >
      {label.name}
    </Chip>
  )
}

/** At most `max` labels, the rest behind "+n" in a popover (design §4.2). */
export function LabelChipList({ labels, max = 2 }: { labels: LabelLike[]; max?: number }) {
  if (labels.length === 0) return null
  const hidden = labels.slice(max)
  return (
    <span className="flex flex-wrap items-center gap-1">
      {labels.slice(0, max).map((label) => (
        <LabelChip key={label.id} label={label} />
      ))}
      {hidden.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Nog ${hidden.length} ${hidden.length === 1 ? 'label' : 'labels'}`}
              className="rounded px-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              +{hidden.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-auto max-w-xs flex-wrap gap-1 p-2">
            {hidden.map((label) => (
              <LabelChip key={label.id} label={label} />
            ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </span>
  )
}
